import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { formatDate } from '@/utils/format';

type PayerContract = {
  id: number;
  payer_name: string;
  payer_code: string;
  contract_type: 'insurer_in_network' | 'insurer_out_of_network' | 'employer' | 'public_payer';
  effective_from: string;
  effective_to: string | null;
  reimbursement_currency: string;
  fee_schedule: Record<string, string>;  // procedure_id_or_code -> amount
  prior_authorization_required: boolean;
  is_active: boolean;
};

const CONTRACT_TYPES = [
  { code: 'insurer_in_network',     label: 'Insurer (in-network)' },
  { code: 'insurer_out_of_network', label: 'Insurer (out-of-network)' },
  { code: 'employer',               label: 'Employer-sponsored' },
  { code: 'public_payer',           label: 'Public payer (NFZ, NHS, …)' },
];

export function PracticePayerContractsPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'payer-contracts'],
    queryFn:  practice.payerContracts.list as () => Promise<PayerContract[]>,
  });
  const [editing, setEditing] = useState<PayerContract | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="Payer contracts"
        subtitle="Insurance and employer reimbursement agreements."
        actions={
          <Button variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCreating(true)}>
            New contract
          </Button>
        }
      />
      <DataState
        query={q}
        empty={{
          title: 'No payer contracts',
          description: 'Add your insurer or employer agreements so claims can be submitted automatically.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((c) => <Row key={c.id} contract={c} onEdit={() => setEditing(c)} />)}
          </Stack>
        )}
      </DataState>

      {(creating || editing) && (
        <ContractDialog contract={editing}
                        onClose={() => { setEditing(null); setCreating(false); }} />
      )}
    </>
  );
}

function Row({ contract, onEdit }: { contract: PayerContract; onEdit: () => void }) {
  const expired = contract.effective_to &&
    new Date(contract.effective_to).getTime() < Date.now();
  return (
    <Card sx={{ borderColor: expired ? 'warning.main' : 'divider' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <AccountTreeIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {contract.payer_name}
              </Typography>
              <Chip size="small" label={contract.payer_code}
                    variant="outlined" className="tabular" />
              {!contract.is_active && (
                <Chip size="small" label="inactive" variant="outlined" />
              )}
              {expired && (
                <Chip size="small" label="expired" color="warning" variant="outlined" />
              )}
              {contract.prior_authorization_required && (
                <Chip size="small" label="prior auth required" color="warning" variant="outlined" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary"
                        sx={{ textTransform: 'capitalize' }}>
              {contract.contract_type.replace(/_/g, ' ')}
              {' · '}<span className="tabular">
                {formatDate(contract.effective_from)}
                {contract.effective_to ? ` – ${formatDate(contract.effective_to)}` : ' – open'}
              </span>
            </Typography>
          </Box>
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>Edit</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ContractDialog({
  contract, onClose,
}: { contract: PayerContract | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = contract !== null;

  const [payerName, setPayerName] = useState(contract?.payer_name ?? '');
  const [payerCode, setPayerCode] = useState(contract?.payer_code ?? '');
  const [contractType, setContractType] = useState(contract?.contract_type ?? 'insurer_in_network');
  const [effectiveFrom, setEffectiveFrom] = useState(contract?.effective_from ?? '');
  const [effectiveTo, setEffectiveTo] = useState(contract?.effective_to ?? '');
  const [currency, setCurrency] = useState(contract?.reimbursement_currency ?? 'EUR');
  const [feeSchedule, setFeeSchedule] = useState(
    JSON.stringify(contract?.fee_schedule ?? {}, null, 2));
  const [priorAuth, setPriorAuth] = useState(contract?.prior_authorization_required ?? false);
  const [isActive, setIsActive] = useState(contract?.is_active ?? true);

  useEffect(() => {
    setPayerName(contract?.payer_name ?? '');
    setPayerCode(contract?.payer_code ?? '');
    setContractType(contract?.contract_type ?? 'insurer_in_network');
    setEffectiveFrom(contract?.effective_from ?? '');
    setEffectiveTo(contract?.effective_to ?? '');
    setCurrency(contract?.reimbursement_currency ?? 'EUR');
    setFeeSchedule(JSON.stringify(contract?.fee_schedule ?? {}, null, 2));
    setPriorAuth(contract?.prior_authorization_required ?? false);
    setIsActive(contract?.is_active ?? true);
  }, [contract?.id]);

  const saveMut = useApiMutation({
    mutationFn: () => {
      let parsedFees: Record<string, string> = {};
      try { parsedFees = JSON.parse(feeSchedule); } catch {/* validated */}
      const payload = {
        payer_name: payerName,
        payer_code: payerCode,
        contract_type: contractType,
        effective_from: effectiveFrom,
        effective_to: effectiveTo || null,
        reimbursement_currency: currency,
        fee_schedule: parsedFees,
        prior_authorization_required: priorAuth,
        is_active: isActive,
      };
      return isEdit
        ? practice.payerContracts.update(contract!.id, payload)
        : practice.payerContracts.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Contract saved.' : 'Contract created.');
      qc.invalidateQueries({ queryKey: ['practice', 'payer-contracts'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  let feeJsonValid = true;
  try { JSON.parse(feeSchedule); } catch { feeJsonValid = false; }
  const valid = payerName.trim() && payerCode.trim() && effectiveFrom && feeJsonValid;

  return (
    <Dialog open onClose={() => !saveMut.isPending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit payer contract' : 'New payer contract'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={8}>
            <TextField fullWidth required label="Payer name"
                       value={payerName}
                       onChange={(e) => setPayerName(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth required label="Payer code"
                       value={payerCode}
                       onChange={(e) => setPayerCode(e.target.value)}
                       helperText="e.g. NFZ, BUPA, AOK" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Contract type"
                       value={contractType}
                       onChange={(e) => setContractType(
                         e.target.value as PayerContract['contract_type'])}>
              {CONTRACT_TYPES.map((t) => (
                <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth select label="Reimbursement currency"
                       value={currency}
                       onChange={(e) => setCurrency(e.target.value)}>
              {['EUR', 'USD', 'PLN', 'GBP'].map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth required type="date" label="Effective from"
                       value={effectiveFrom}
                       onChange={(e) => setEffectiveFrom(e.target.value)}
                       InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth type="date" label="Effective to (optional)"
                       value={effectiveTo}
                       onChange={(e) => setEffectiveTo(e.target.value)}
                       InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={5} label="Fee schedule (JSON)"
              helperText='Map of procedure ID/code → amount. e.g. {"42": "75.00", "GP_VISIT": "60.00"}'
              error={!feeJsonValid}
              value={feeSchedule}
              onChange={(e) => setFeeSchedule(e.target.value)}
              sx={{ '& textarea': { fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={priorAuth}
                               onChange={(_, v) => setPriorAuth(v)} />}
              label="Prior authorization required" />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={isActive}
                               onChange={(_, v) => setIsActive(v)} />}
              label="Active" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || saveMut.isPending}
                onClick={() => saveMut.mutate()}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
